import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ScrollText, BarChart3, History, LogOut, Settings } from 'lucide-react'
import { SophiaWordmark } from '@/components/SophiaMark'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/store/auth.store'
import { logout } from '@/features/auth/api'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/classes', label: 'Classes', icon: ScrollText },
  { to: '/benchmarks', label: 'Bases de teste', icon: BarChart3 },
  { to: '/logs', label: 'Logs', icon: History },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
]

export function AppShell() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user, reset } = useAuthStore()

  async function handleLogout() {
    await logout().catch(() => undefined)
    qc.setQueryData(['auth', 'me'], null)
    reset()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-bg-app">
      <aside
        className="fixed inset-y-0 left-0 flex flex-col border-r border-border-subtle bg-bg-surface"
        style={{ width: 'var(--shell-sidebar-w)' }}
      >
        <div className="px-4 h-[var(--shell-topbar-h)] flex items-center border-b border-border-subtle">
          <SophiaWordmark />
        </div>

        <nav className="flex-1 p-3 flex flex-col gap-0.5">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 h-9 rounded-md text-sm font-medium',
                  'transition-colors duration-fast ease-out',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-fg-2 hover:bg-neutral-50',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border-subtle">
          <div className="px-3 py-2 mb-2 flex flex-col gap-0.5">
            <span className="text-sm font-medium text-fg-1 truncate">{user?.nome ?? '—'}</span>
            <span className="text-xs text-fg-3 truncate">{user?.email}</span>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      </aside>

      <main style={{ marginLeft: 'var(--shell-sidebar-w)' }}>
        <div className="max-w-[var(--container-max)] mx-auto px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
